import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSwipeToClose } from '@/hooks/ui/useSwipeToClose';
import { useBottomNavSafe } from '@/context/BottomNavContext';
import { useMobileAIChat } from '@/context/MobileAIChatContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { useAuth } from '@/context/AuthContext';
import { useAIChat } from '../useAIChat';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { cn } from '@/lib/core/utils';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import type { AIToolCall, AIMessage } from '../types';

// Import mobile-specific components
import { MobileChatHeader } from './MobileChatHeader';
import { MobileChatHistory } from './MobileChatHistory';
import { MobileMessagesContainer } from './MobileMessagesContainer';
import { MobileChatInputArea } from './MobileChatInputArea';
import { useMobileChatScroll } from './hooks/useMobileChatScroll';
import { Button } from '@/components/ui/button';

interface MobileAIChatOverlayProps {
  businessId: string | null;
}

/**
 * Mobile AI Chat Overlay.
 *
 * Positioning strategy for iOS keyboard compatibility:
 * - Uses visualViewport API to track the actual visible viewport
 * - Direct DOM manipulation for smooth, lag-free height updates
 * - requestAnimationFrame for sync with browser repaints
 * - This is how all mobile chat apps handle keyboards
 */
export const MobileAIChatOverlay: React.FC<MobileAIChatOverlayProps> = ({ businessId }) => {
  const isMobile = useIsMobile();
  const { isOpen, closeAIChat, pendingMessage, setPendingMessage } = useMobileAIChat();
  const bottomNav = useBottomNavSafe();
  const contentRef = useRef<HTMLDivElement>(null);

  // Business context
  const { selectedBusiness } = useBusinessContext();
  const { session } = useAuth();

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // Hide bottom nav when overlay opens
  useEffect(() => {
    if (!isMobile || !bottomNav) return;

    if (isOpen) {
      bottomNav.setVisible(false);
    } else {
      bottomNav.setVisible(true);
    }
  }, [isOpen, isMobile, bottomNav]);

  // Track visual viewport - DIRECT DOM manipulation for smooth updates
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const vv = window.visualViewport;
    if (!vv) return;

    let rafId: number | null = null;

    const updateViewport = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.style.height = `${vv.height - 24}px`;
        }
        rafId = null;
      });
    };

    // Initial height
    updateViewport();

    // Listen to visual viewport changes (keyboard open/close)
    vv.addEventListener('resize', updateViewport);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      vv.removeEventListener('resize', updateViewport);
    };
  }, [isOpen]);

  // Swipe-to-close gesture
  const { swipeOffset, isDragging } = useSwipeToClose({
    onClose: closeAIChat,
    enabled: isOpen,
    threshold: 100,
    hapticFeedback: true,
    contentRef,
  });

  // AI Chat logic from existing hook (no changes to API)
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    confirmTools,
    cancelPendingTools,
    sendQuestionAnswers,
    conversationSummaries,
    activeConversationId,
    createNewConversation,
    switchConversation,
    deleteConversation,
    stopGeneration,
    retryLastMessage,
    canRetry,
    wasStopped,
    streamingContent,
    resendMessage,
  } = useAIChat(businessId, session?.access_token);

  // Mobile scroll management
  const {
    containerRef: scrollContainerRef,
    autoScrollEnabled,
    setAutoScrollEnabled,
    scrollToBottom,
    handleScroll,
  } = useMobileChatScroll(isLoading, streamingContent);

  // Messages end ref for scroll targeting
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // View state
  const [currentView, setCurrentView] = useState<'chat' | 'history'>('chat');

  // Handlers
  const handleSelectConversation = useCallback((id: string) => {
    switchConversation(id);
    setCurrentView('chat');
  }, [switchConversation]);

  const handleNewChat = useCallback(() => {
    createNewConversation();
    setCurrentView('chat');
  }, [createNewConversation]);

  const handleDeleteConversation = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteConversation(id);
  }, [deleteConversation]);

  const handleRevert = useCallback(async (toolCall: AIToolCall) => {
    const resourceId = toolCall.result?.shift?.id || toolCall.result?.employee?.id;
    const resourceType = toolCall.result?.shift ? 'shift' : 'employee';
    if (!resourceId) return;
    await sendMessage(`Please delete the ${resourceType} with ID ${resourceId} that was just created.`);
  }, [sendMessage]);

  const handleSuggestionClick = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  const handleResend = useCallback((message: AIMessage) => {
    resendMessage(message);
  }, [resendMessage]);

  const handleEdit = useCallback((message: AIMessage, newContent: string) => {
    resendMessage(message, newContent);
  }, [resendMessage]);

  // Auto-send pending message when overlay opens
  useEffect(() => {
    if (isOpen && pendingMessage && businessId && !isLoading) {
      const timer = setTimeout(() => {
        sendMessage(pendingMessage);
        setPendingMessage(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, pendingMessage, businessId, isLoading, sendMessage, setPendingMessage]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (autoScrollEnabled) {
      scrollToBottom(true);
    }
  }, [messages, autoScrollEnabled, scrollToBottom]);

  if (!isMobile || !isOpen) return null;

  return createPortal(
    <div
      ref={contentRef}
      className="fixed left-0 right-0 bottom-0 z-dropdown bg-background flex flex-col rounded-t-2xl shadow-2xl"
      style={{
        // Leave a gap at top to hint it's a sheet
        height: `${(window.visualViewport?.height || window.innerHeight) - 24}px`,
        // Smooth height transitions
        transition: 'height 0.25s ease-out',
        // Hint to browser for smoother animations
        willChange: 'height',
        // Safe area insets
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Header */}
      <div className="shrink-0 border-b bg-background">
        {/* Drag handle */}
        <div className="shrink-0 flex justify-center pt-3 pb-1" data-slot="sheet-drag-handle">
          <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30 cursor-pointer" />
        </div>

        <MobileChatHeader
          currentView={currentView}
          onViewChange={setCurrentView}
          onNewChat={handleNewChat}
          onClose={closeAIChat}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 min-h-0">
        {currentView === 'history' ? (
          <MobileChatHistory
            conversations={conversationSummaries}
            activeConversationId={activeConversationId}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={handleDeleteConversation}
          />
        ) : (
          <>
            {/* Messages */}
            {!businessId ? (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center ring-1 ring-border mb-3">
                    <img src="/media/tag-innovation-logo.png" alt={AI_ASSISTANT.name} className="h-7 w-7 rounded-sm" />
                  </div>
                  <p className="text-base">Select a business first</p>
                  <p className="text-sm mt-1">Go to your dashboard and select a business</p>
                </div>
              </div>
            ) : (
              <MobileMessagesContainer
                containerRef={scrollContainerRef}
                messagesEndRef={messagesEndRef}
                autoScrollEnabled={autoScrollEnabled}
                onScroll={handleScroll}
                messages={messages}
                isLoading={isLoading}
                onRevert={handleRevert}
                onConfirmTools={confirmTools}
                onCancelTools={cancelPendingTools}
                onSuggestionClick={handleSuggestionClick}
                businessLogoUrl={selectedBusiness?.favicon_url}
                businessId={businessId}
                onQuestionsSubmit={sendQuestionAnswers}
                streamingContent={streamingContent}
                onResend={handleResend}
                onEdit={handleEdit}
                conversationId={activeConversationId}
              />
            )}

            {/* Error display with retry */}
            {(error || wasStopped) && (
              <div className="shrink-0 px-4 py-2 bg-destructive/10 text-destructive text-sm flex items-center justify-between gap-2">
                <span>{error || 'Generation stopped'}</span>
                {canRetry && (
                  <Button
                    onClick={retryLastMessage}
                    className="h-7 px-2 text-xs border rounded hover:bg-destructive/20"
                  >
                    <RotateCcw className="h-3 w-3 mr-1 inline" />
                    Retry
                  </Button>
                )}
              </div>
            )}

            {/* Input */}
            <div className="shrink-0">
              <MobileChatInputArea
                onSend={sendMessage}
                onStop={stopGeneration}
                disabled={isLoading || !businessId}
                isLoading={isLoading}
                placeholder={businessId ? 'Ask, search or make anything...' : 'Select a business first'}
                error={error || (wasStopped ? 'Generation stopped' : null)}
                canRetry={canRetry}
                onRetry={retryLastMessage}
                allowFileUpload={!!businessId}
              />
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MobileAIChatOverlay;

import { RotateCcw } from 'lucide-react';
